/**
 * Tuning for the instanced activities (research 01 §7.2, §8 and 02 §5.5–5.8,
 * §11.7–11.8). Everything a designer may want to tweak lives here.
 */

/** Rift Delve (≈ Chaos Dungeon). */
export const RIFT = {
  /** Total run timer in seconds (the lead's spec: 5 minutes for all three floors). */
  timeLimit: 300,
  /** Gauge at which the portal to the next floor opens (floor 1 → 2, floor 2 → 3). */
  portalAt: [20, 50] as const,
  /** Gauge cap while on each floor, so progress must continue on the next floor. */
  floorCap: [30, 65, 100] as const,
  /** Gauge per kill (research 02 §11.8: trash 0.3, elite 3, floor boss 8, core 5, mini-boss 4). */
  weights: { minion: 0.3, normal: 0.45, elite: 3, mini: 4, core: 5, boss: 8 },
  /** Floor 2: the Rift Warden rises at this gauge. */
  wardenAt: 35,
  /** Floor 3: Voidmaw appears after this many cores (or at `voidmawAt` gauge). */
  voidmawCores: 3,
  voidmawAt: 80,
  level: 30,
  /** Rift Energy per rewarded run. */
  energyCost: 50,
  /** Alive trash the spawner keeps up per floor (+ `perExtraPlayer` per extra party member). */
  population: [14, 16, 12] as const,
  perExtraPlayer: 4,
  packSize: [
    [4, 6],
    [4, 7],
    [3, 5],
  ] as const,
  /** Seconds between pack spawns while below the population target. */
  packEvery: 1.2,
  /** First elite and interval per floor. */
  eliteFirst: [14, 6, 20] as const,
  eliteEvery: [24, 17, 28] as const,
  maxElites: 2,
  /** Wind orbs float in every so often; elites drop health / power orbs. */
  orbEvery: 22,
  orbLife: 30,
  pickupRadius: 1.8,
  portalRadius: 3,
  /** Seconds between "portal entered" and every peer moving on. */
  moveDelay: 2.2,
} as const;

/** Colossus Hunt (≈ Guardian Raid, launch rules). */
export const COLOSSUS = {
  timeLimit: 20 * 60,
  revives: 3,
  level: 30,
  /** Revive HP fraction (in place). */
  reviveHp: 0.6,
  /** First-clear gold by colossus tier (research 02 §11.7). */
  firstClearGold: { cinderhide: 100, gloomfang: 150, rimebound: 200, dunestrider: 250 } as Record<string, number>,
} as const;

/** Deepvault / Warlord Siege gates. */
export const GATE = {
  level: 30,
  /** Seconds after the soft enrage (boss script `enrageAfter`) before the boss wipes the raid. */
  berserkGrace: 150,
  /** Seconds everyone may lie dead before the gate resets (Siege Normal waits longer for revives). */
  wipeGrace: 4,
  wipeGraceRevive: 8,
  /** Seconds between a wipe and the gate being ready again. */
  resetDelay: 4,
  /** Siege Normal: one revive per player per gate attempt. */
  siegeNormalRevives: 1,
  reviveHp: 0.5,
} as const;

/** The Crucible (3v3 elimination, best of five). */
export const CRUCIBLE = {
  roundsToWin: 3,
  maxRounds: 5,
  /** Longest wait for the other players to load in (the match starts as soon as everyone is there). */
  warmup: 25,
  countdown: 4,
  roundTime: 100,
  roundEnd: 4,
  /** Crucible Crests per result (practice vs. sparring knights pays less). */
  crests: { win: 30, loss: 10, draw: 15, practiceWin: 5, practiceLoss: 2 },
  silver: { win: 2000, loss: 800, draw: 1200 },
} as const;
